import { Injectable, InjectionToken } from '@angular/core';
import PocketBase, { AuthModel } from 'pocketbase';
import { BehaviorSubject, defer, filter, from, map, of, switchMap } from 'rxjs';

import { environment } from '../../environments/environment';
import { SuppliersResponse, UsersResponse, UsersRoleOptions } from '../types/pocketbase-types';

export type ExpandedUsersResponse = UsersResponse<{ company: SuppliersResponse }>;

export const ME = new InjectionToken<ExpandedUsersResponse>('USER');

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  pb = new PocketBase(environment.pocketBase.url);

  adminLogin(username: string, password: string) {
    return defer(() => from(this.pb.admins.authWithPassword(username, password)));
  }

  login(username: string, password: string) {
    return defer(() =>
      from(
        this.pb.collection('users').authWithPassword(username, password, {
          expand: 'company',
        }),
      ),
    );
  }

  changeNotificationsEmail(newEmail: string) {
    const user$ = this.getMe();

    return user$.pipe(
      map((user) => user.id),
      switchMap((userId) =>
        this.pb.collection('users').update(userId, {
          notification_email: newEmail,
        }),
      ),
    );
  }

  toggleNotifications(enabled: boolean) {
    const user$ = this.getMe();

    return user$.pipe(
      map((user) => user.id),
      switchMap((userId) =>
        this.pb.collection('users').update(userId, {
          notifications: enabled,
        }),
      ),
    );
  }

  forgotPassword(email: string) {
    return defer(() => from(this.pb.collection('users').requestPasswordReset(email)));
  }

  changePassword(userId: string, props: { oldPassword: string; password: string; passwordConfirm: string }) {
    return defer(() => from(this.pb.collection('users').update(userId, props)));
  }

  confirmPasswordChange(token: string, password: string, passwordConfirm: string) {
    return defer(() => from(this.pb.collection('users').confirmPasswordReset(token, password, passwordConfirm)));
  }

  logOut() {
    this.pb.authStore.clear();
    window.location.href = '/';
  }

  getMe$ = new BehaviorSubject<UsersResponse | ExpandedUsersResponse | undefined>(undefined);

  getMe() {
    let userModel: AuthModel | undefined = this.pb.authStore.model;
    this.pb.authStore.onChange(() => {
      userModel = this.pb.authStore.model;
      if (userModel && !this.getMe$.value) {
        this.getMe$.next(userModel as UsersResponse);
      }
    }, true);

    return of(userModel).pipe(filter((user): user is UsersResponse | ExpandedUsersResponse => !!user));
  }

  getMyRole() {
    return this.getMe().pipe(map((user) => user.role));
  }

  readonly iAmAdmin$ = this.getMe$.pipe(map((user) => user?.role === UsersRoleOptions.admin));
}
